import { useState, useEffect } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Zap, Loader2, Building2 } from "lucide-react";

const ANY = "__any__";

export const SnapPartnerDialog = ({ leadId, partners = [], onDone, size = "sm", variant = "outline", label = "SNAP to partner", className = "" }) => {
  const [open, setOpen] = useState(false);
  const [orgId, setOrgId] = useState("");
  const [userId, setUserId] = useState(ANY);
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !orgId) { setMembers([]); setUserId(ANY); return; }
    setLoadingMembers(true);
    api.get(`/leads/${leadId}/partner-members/${orgId}`)
      .then(({ data }) => setMembers(data))
      .catch(() => setMembers([]))
      .finally(() => setLoadingMembers(false));
  }, [open, orgId, leadId]);

  const submit = async () => {
    if (!orgId) return toast.error("Choose a partner agency");
    setBusy(true);
    try {
      await api.post(`/leads/${leadId}/snap-partner`, { org_id: orgId, user_id: userId === ANY ? null : userId });
      const agency = partners.find((p) => p.other_org_id === orgId)?.other_org_name || "partner";
      const person = userId === ANY ? "" : ` · ${members.find((m) => m.user_id === userId)?.name || ""}`;
      toast.success(`Snapped to ${agency}${person} — they've been notified`);
      setOpen(false); setOrgId(""); setUserId(ANY);
      onDone && onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button variant={variant} size={size} className={className} onClick={(e) => { e.stopPropagation(); setOpen(true); }} data-testid={`snap-partner-open-${leadId}`}>
        <Building2 className="mr-1.5 h-4 w-4" /> {label}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" onClick={(e) => e.stopPropagation()} data-testid="snap-partner-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading"><Zap className="h-5 w-5 text-primary" /> Snap to a partner agency</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">Share this lead with a connected partner and notify them instantly. Optionally send it to a specific person there.</p>
          <div className="space-y-4">
            <div>
              <Label>Partner agency</Label>
              <Select value={orgId} onValueChange={(v) => { setOrgId(v); setUserId(ANY); }}>
                <SelectTrigger className="mt-1.5" data-testid="snap-partner-agency-select"><SelectValue placeholder="Choose a connected agency" /></SelectTrigger>
                <SelectContent>
                  {partners.map((p) => <SelectItem key={p.other_org_id} value={p.other_org_id}>{p.other_org_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Specific person <span className="font-normal text-muted-foreground">(optional)</span></Label>
              <Select value={userId} onValueChange={setUserId} disabled={!orgId || loadingMembers}>
                <SelectTrigger className="mt-1.5" data-testid="snap-partner-person-select">
                  <SelectValue placeholder={loadingMembers ? "Loading members…" : "Whole agency"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY}>Whole agency (admins & managers notified)</SelectItem>
                  {members.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name} · {m.role}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy} data-testid="snap-partner-cancel">Cancel</Button>
            <Button onClick={submit} disabled={busy || !orgId} data-testid="snap-partner-confirm">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Snap to partner</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
